import React, { useEffect } from 'react';
import { Bell, RefreshCcw, UserPlus } from 'lucide-react';
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/use-toast";
import ThemeSwitcher from '@/components/theme/ThemeSwitcher';

// Import our components
import JournalistSidebar from '@/components/reader-home/JournalistSidebar';
import SearchBar from '@/components/reader-home/SearchBar';
import TrendingTopics from '@/components/reader-home/TrendingTopics';
import QuickFilters from '@/components/reader-home/QuickFilters';
import ContentSection from '@/components/reader-home/ContentSection';
import ForYouSection from '@/components/reader-home/ForYouSection';
import { useReaderHomeService } from '@/hooks/reader-home/useReaderHomeService';
import { useAuth } from '@/hooks/useAuth';
import { getGreeting } from '@/components/reader-home/mockData';
import { useNavigate } from 'react-router-dom';

const ReaderHome: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState("following");
  const { userProfile, user } = useAuth();
  const navigate = useNavigate();
  const {
    articles,
    followedJournalists,
    topics,
    loading,
    error,
    refreshData,
    toggleFollowJournalist
  } = useReaderHomeService();

  // Debug output
  useEffect(() => {
    console.log('ReaderHome - User:', user?.id);
    console.log('ReaderHome - Followed Journalists:', followedJournalists);
  }, [user, followedJournalists]);

  // Get user's first name or display name
  const userName = userProfile?.first_name || 
    (userProfile?.display_name?.split(' ')[0]) || 
    'Reader';

  // Handle refresh button click
  const handleRefresh = () => {
    refreshData();
    toast({
      title: "Refreshing content",
      description: "Loading the latest articles and updates"
    });
  };

  // Filter articles for the "following" tab
  const followedArticles = articles.filter(article => 
    followedJournalists.some(j => j.id === article.authorId)
  );

  // For the trending tab, sort by highest read_time or other metric
  const trendingArticles = [...articles].sort((a, b) => 
    (b.progress || 0) - (a.progress || 0)
  );
  
  // For the discover tab, show articles not from followed journalists
  const discoverArticles = articles.filter(article => 
    !followedJournalists.some(j => j.id === article.authorId)
  );

  // Get journalists for suggestions (those not already followed)
  const suggestedJournalists = articles
    .filter(article => !followedJournalists.some(j => j.id === article.authorId))
    .map(article => ({
      id: article.authorId,
      name: article.author,
      avatar: '',
      bio: '',
      followers: 0,
      topics: article.topics,
      unread: 0
    }))
    .filter((journalist, index, self) => 
      index === self.findIndex((j) => j.id === journalist.id)
    );

  // Handle topic click
  const handleTopicClick = (topic: string) => {
    toast({
      title: "Topic selected",
      description: `You selected the topic: ${topic}`,
    });
    navigate(`/trending?topic=${encodeURIComponent(topic)}`);
  };

  // Handle follow journalist
  const handleFollowJournalist = (journalistId: string) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to follow journalists",
        variant: "destructive"
      });
      return;
    }
    
    console.log('Following journalist with ID:', journalistId);
    toggleFollowJournalist(journalistId);
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar - Followed Journalists */}
      <JournalistSidebar 
        journalists={followedJournalists} 
        suggestedJournalists={suggestedJournalists}
        loading={loading} 
        onFollowJournalist={handleFollowJournalist}
        onSeeAllClick={() => setActiveTab("following")}
      />

      {/* Main Content */}
      <main className="flex-1 p-4 md:p-6 max-w-7xl mx-auto">
        {/* Header with greeting and search */}
        <header className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl md:text-3xl font-bold">
                {getGreeting()}, <span className="text-primary">{userName}</span>
              </h1>
              <div className="md:hidden">
                <ThemeSwitcher />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleRefresh}
                disabled={loading}
              >
                <RefreshCcw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <div className="hidden md:block">
                <ThemeSwitcher />
              </div>
              <SearchBar topics={topics} journalists={followedJournalists} />
            </div>
          </div>
        </header>

        {/* Error display */}
        {error && (
          <div className="p-4 mb-6 bg-destructive/10 text-destructive rounded-md">
            <p>Error loading content. Please try refreshing the page.</p>
          </div>
        )}

        {/* Trending Topics Section */}
        <TrendingTopics 
          topics={topics} 
          loading={loading} 
          onTopicClick={handleTopicClick}
        />

        {/* Quick Filters */}
        <QuickFilters activeTab={activeTab} setActiveTab={setActiveTab} />

        {/* Content Tabs */}
        <Tabs defaultValue="following" value={activeTab} onValueChange={setActiveTab}>
          <TabsContent value="following" className="mt-0">
            {loading ? (
              <div className="space-y-4">
                <Skeleton className="h-10 w-72" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Skeleton className="h-[300px] rounded-md" />
                  <Skeleton className="h-[300px] rounded-md" />
                  <Skeleton className="h-[300px] rounded-md" />
                </div>
              </div>
            ) : (
              <ContentSection 
                title="New from Journalists You Follow"
                articles={followedArticles}
                emptyStateMessage="No articles yet from journalists you follow"
                emptyStateAction={
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground mb-3">
                      Follow some journalists to see their content here
                    </p>
                    <Button 
                      onClick={() => setActiveTab("discover")}
                      variant="outline"
                    >
                      <UserPlus className="mr-2 h-4 w-4" />
                      Discover Journalists
                    </Button>
                  </div>
                }
                suggestedArticles={articles.slice(0, 3)}
              />
            )}
          </TabsContent>
          
          <TabsContent value="trending" className="mt-0">
            {loading ? (
              <div className="space-y-4">
                <Skeleton className="h-10 w-72" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Skeleton className="h-[300px] rounded-md" />
                  <Skeleton className="h-[300px] rounded-md" />
                  <Skeleton className="h-[300px] rounded-md" />
                </div>
              </div>
            ) : (
              <ContentSection 
                title="Trending Articles"
                articles={trendingArticles}
                emptyStateMessage="No trending articles available at the moment"
                suggestedArticles={articles}
              />
            )}
          </TabsContent>
          
          <TabsContent value="discover" className="mt-0">
            {loading ? (
              <div className="space-y-4">
                <Skeleton className="h-10 w-72" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Skeleton className="h-[300px] rounded-md" />
                  <Skeleton className="h-[300px] rounded-md" />
                  <Skeleton className="h-[300px] rounded-md" />
                </div>
              </div>
            ) : (
              <ContentSection 
                title="Discover New Content"
                articles={discoverArticles}
                emptyStateMessage="No new content to discover at the moment"
                suggestedArticles={articles}
              />
            )}
          </TabsContent>
        </Tabs>
        
        {/* For You Section */}
        {loading ? (
          <div className="mt-12 space-y-4">
            <Skeleton className="h-10 w-72" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Skeleton className="h-[250px] rounded-md" />
              <Skeleton className="h-[250px] rounded-md" />
            </div>
          </div>
        ) : (
          <ForYouSection articles={articles.slice(0, 2)} />
        )}
      </main>

      {/* Notifications panel - mobile only */}
      <div className="fixed bottom-4 right-4 md:hidden flex gap-3">
        <Button size="icon" className="rounded-full h-12 w-12 shadow-lg">
          <Bell className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};

export default ReaderHome;
